import React from 'react';
import { ActionIcon, Badge, Box, Button, Collapse, Flex, Group, Loader, Menu, NumberInput, Overlay, Paper, Select, Stack, Text, TextInput, Title, Tooltip as MantineTooltip } from '@mantine/core';
import { IconCalculator, IconPrinter, IconChevronDown, IconDeviceFloppy, IconSend, IconTrash, IconPlayerPlay, IconPlayerStop, IconArrowBack, IconCheck, IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { useLocalStorage, useHotkeys } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useQueryClient } from '@tanstack/react-query';
import { OTPApi } from '../../../global/apis/OTPApi/OTPApi';
import { MMEApi } from '../../../global/apis/MMEApi/MMEApi';
import { ITaperingStep, IStepMedication, IOTP, OTPStatus } from '../../../global/entities/OTP/OTP';
import { IDoseSuggestionResult, IOpioidDrugRouteCombination } from '../../../global/entities/MME/MME';
import { useGetSavedMMECalculations } from '../../../global/entities/MME/queries/useGetSavedMMECalculations';
import { PatientStore } from '../../../global/Store/PatientStore';
import { QueryKeys } from '../../../global/queryDefaults/QueryKeys';
import { useStepsHistory } from '../../../global/hooks/useStepsHistory';
import TaperingStepsTable from './TaperingStepsTable';
import {
    useDeleteOTP,
    useAbortOTP,
    useAppendOTP,
    useCompleteOTP,
    useActivateOTP,
    useSubmitOTPForValidation,
    useDelegateOTPForValidation,
    useValidateOTPByDelegatedHcp,
    useReturnOTPToDraft,
    useDownloadOTPPdf,
} from '../../../global/entities/OTP/mutations';
import { OTPEventTimeline } from './OTPEventTimeline';
import { AppMedWarning } from '../Graph/components/AppMedWarning';
import { CoMedicationChecklistModal } from './OTPCoMedicationChecklist/CoMedicationChecklistModal';
import { PatientAcknowledgmentModal } from './PatientAcknowledgmentModal';
import { OTPOutcomeDialog, OutcomeAction, IOutcomeDialogResult } from './OTPPlanManager/OTPOutcomeDialog';
import { ValidationResponsibilityDialog, IValidationResponsibilityResult } from './OTPPlanManager/ValidationResponsibilityDialog';
import {
    type ChartDataPoint,
    convertTaperingStepsToChartData,
    formatDateTick,
    getStatusColor,
    useGetStatusLabel,
    roundToNearestHalf,
    computeVirtualMMEArray,
    adjustStepsForVirtualMME,
    CustomActualMMEDot,
    CustomActualMMEActiveDot,
    CustomTooltip,
} from './OTPChartHelpers';

interface TaperingParameters {
    originalMME: number;
    savedCalculationId: number | null;
    reductionPercentage: number;
    daysPerStep: number;
    targetFinalMME: number;
}

interface IOTPParametersSectionProps {
    patientStore?: PatientStore;
    onNavigateToMMETab?: () => void;
}

const OTPParametersSectionComponent: React.FC<IOTPParametersSectionProps> = inject('patientStore')(
    observer(({ patientStore, onNavigateToMMETab }) => {
        const { t, i18n } = useTranslation();
        const queryClient = useQueryClient();
        const [isLoading, setIsLoading] = React.useState(false);
        const [taperingData, setTaperingData] = React.useState<ChartDataPoint[]>([]);

        // Collapsed states with localStorage persistence
        const [isStatusExpanded, setIsStatusExpanded] = useLocalStorage({ key: 'otp_status_expanded', defaultValue: true });
        const [isParametersExpanded, setIsParametersExpanded] = useLocalStorage({ key: 'otp_parameters_expanded', defaultValue: true });
        const [isGraphExpanded, setIsGraphExpanded] = useLocalStorage({ key: 'otp_graph_expanded', defaultValue: true });

        // Local steps state — after initial generation, steps are managed locally
        const [localSteps, setLocalSteps] = React.useState<ITaperingStep[]>([]);
        const [isCustomized, setIsCustomized] = React.useState(false);

        // Drug selection state
        const [drugRouteCombinations, setDrugRouteCombinations] = React.useState<IOpioidDrugRouteCombination[]>([]);
        const [selectedDrugRouteKey, setSelectedDrugRouteKey] = React.useState<string | null>(null);
        const [doseSuggestions, setDoseSuggestions] = React.useState<IDoseSuggestionResult[]>([]);
        const doseSuggestionsRef = React.useRef<IDoseSuggestionResult[]>(doseSuggestions);
        doseSuggestionsRef.current = doseSuggestions;

        // Undo/redo history for step edits
        const stepsHistory = useStepsHistory<ITaperingStep[]>();

        // Fetch saved MME calculations for the patient
        const patientId = patientStore?.patientId ? Number(patientStore.patientId) : undefined;
        const { data: savedCalculations = [], isLoading: isLoadingCalculations } = useGetSavedMMECalculations(patientId);

        // Tapering parameters state
        const [parameters, setParameters] = React.useState<TaperingParameters>({
            originalMME: 100,
            savedCalculationId: null,
            reductionPercentage: 10,
            daysPerStep: 7,
            targetFinalMME: 1,
        });

        // OTP save state
        const [otpId, setOtpId] = React.useState<number | null>(null);
        const [isSaving, setIsSaving] = React.useState(false);

        // Track initial saved state for change detection
        const [savedState, setSavedState] = React.useState<{
            parameters: TaperingParameters;
            steps: ITaperingStep[];
            drugRouteKey: string | null;
        } | null>(null);

        // Current plan state (single plan per patient)
        const [currentPlan, setCurrentPlan] = React.useState<IOTP | null>(null);
        const [isLoadingPlan, setIsLoadingPlan] = React.useState(false);

        const isDraftMode = currentPlan === null || currentPlan.status === OTPStatus.Draft;

        // Show overlay when no MME calculations exist and no plan is loaded
        const showNoMMEOverlay = !isLoadingCalculations && !isLoadingPlan && savedCalculations.length === 0 && !currentPlan;

        // Measure the full graph container width to scale the mini chart proportionally
        const graphContainerRef = React.useRef<HTMLDivElement>(null);
        const [graphContainerWidth, setGraphContainerWidth] = React.useState(0);
        React.useEffect(() => {
            const el = graphContainerRef.current;
            if (!el) return;
            const observer = new ResizeObserver((entries) => {
                for (const entry of entries) 
                    setGraphContainerWidth(entry.contentRect.width);
                
            });
            observer.observe(el);
            return () => observer.disconnect();
        }, []);

        // When no plan is loaded, force parameters open and graph collapsed
        React.useEffect(() => {
            if (!currentPlan) {
                setIsStatusExpanded(false);
                setIsParametersExpanded(true);
                setIsGraphExpanded(false);
            }
        }, [currentPlan, setIsStatusExpanded, setIsParametersExpanded, setIsGraphExpanded]);

        // Auto-expand graph when steps are generated and no plan is saved yet
        React.useEffect(() => {
            if (!currentPlan && localSteps.length > 0)
                setIsGraphExpanded(true);

        }, [localSteps, currentPlan, setIsGraphExpanded]);

        // Co-medication checklist modal state
        const [coMedicationModalOpen, setCoMedicationModalOpen] = React.useState(false);
        const [pendingSubmitPlan, setPendingSubmitPlan] = React.useState<IOTP | null>(null);

        // Patient acknowledgment modal state
        const [acknowledgmentModalOpen, setAcknowledgmentModalOpen] = React.useState(false);

        // Validation responsibility dialog state
        const [validationResponsibilityDialogOpen, setValidationResponsibilityDialogOpen] = React.useState(false);

        // Mutation hooks for plan actions
        const deleteOTPMutation = useDeleteOTP(patientId);
        const abortOTPMutation = useAbortOTP(patientId);
        const appendOTPMutation = useAppendOTP(patientId);
        const completeOTPMutation = useCompleteOTP(patientId);
        const activateOTPMutation = useActivateOTP(patientId);
        const submitForValidationMutation = useSubmitOTPForValidation(patientId);
        const delegateForValidationMutation = useDelegateOTPForValidation(patientId);
        const validateByDelegatedHcpMutation = useValidateOTPByDelegatedHcp(patientId);
        const returnToDraftMutation = useReturnOTPToDraft(patientId);
        const downloadPdfMutation = useDownloadOTPPdf();

        // Outcome dialog state
        const [outcomeDialogOpen, setOutcomeDialogOpen] = React.useState(false);
        const [outcomeDialogAction, setOutcomeDialogAction] = React.useState<OutcomeAction>('complete');
        const [pendingOutcomePlan, setPendingOutcomePlan] = React.useState<IOTP | null>(null);

        // Fetch drug/route combinations on mount
        React.useEffect(() => {
            const fetchCombinations = async () => {
                try {
                    const combinations = await MMEApi.getDrugRouteCombinations();
                    setDrugRouteCombinations(combinations);
                } catch (error) {
                    console.error('Error fetching drug/route combinations:', error);
                }
            };
            fetchCombinations();
        }, []);

        // Helper function to load plan data into component state
        const loadPlanIntoState = React.useCallback((plan: IOTP) => {
            stepsHistory.clear();
            setOtpId(plan.id);

            // Populate parameters from loaded OTP
            if (plan.parameters) {
                setParameters({
                    originalMME: plan.originalMME,
                    savedCalculationId: plan.savedMMECalculationID,
                    reductionPercentage: plan.parameters.reductionPercentage,
                    daysPerStep: plan.parameters.daysPerStep,
                    targetFinalMME: plan.parameters.targetFinalMME ?? 1,
                });

                // Restore drug/route selection if saved
                if (plan.parameters.selectedDrugID && plan.parameters.selectedRouteID) {
                    setSelectedDrugRouteKey(
                        `${plan.parameters.selectedDrugID}-${plan.parameters.selectedRouteID}`
                    );
                } else setSelectedDrugRouteKey(null);
            }

            // Populate steps from loaded OTP
            if (plan.taperingSteps && plan.taperingSteps.length > 0) {
                setLocalSteps(plan.taperingSteps);
                setIsCustomized(true);

                // Update chart data
                const chartData = convertTaperingStepsToChartData(
                    plan.taperingSteps,
                    i18n.language
                );
                setTaperingData(chartData);
            } else {
                setLocalSteps([]);
                setTaperingData([]);
                setIsCustomized(false);
            }

            // Save initial state for change detection
            const loadedDrugRouteKey = plan.parameters?.selectedDrugID && plan.parameters?.selectedRouteID
                ? `${plan.parameters.selectedDrugID}-${plan.parameters.selectedRouteID}`
                : null;

            setSavedState({
                parameters: {
                    originalMME: plan.originalMME,
                    savedCalculationId: plan.savedMMECalculationID,
                    reductionPercentage: plan.parameters?.reductionPercentage ?? 10,
                    daysPerStep: plan.parameters?.daysPerStep ?? 7,
                    targetFinalMME: plan.parameters?.targetFinalMME ?? 1,
                },
                steps: plan.taperingSteps ?? [],
                drugRouteKey: loadedDrugRouteKey,
            });
        }, [i18n.language]);

        // Helper to reload the current plan from the API
        const reloadCurrentPlan = React.useCallback(async () => {
            if (!patientId) return;
            setIsLoadingPlan(true);
            try {
                const plan = await OTPApi.getCurrentOTPByPatientId(patientId);
                if (plan) {
                    setCurrentPlan(plan);
                    loadPlanIntoState(plan);
                } else 
                    setCurrentPlan(null);
                
            } catch (error) {
                console.error('Error reloading current plan:', error);
            } finally {
                setIsLoadingPlan(false);
                queryClient.invalidateQueries({ queryKey: [QueryKeys.otpByPatient, patientId] });
            }
        }, [patientId, loadPlanIntoState, queryClient]);

        // Load current plan for the patient on mount
        React.useEffect(() => {
            if (!patientId) return;
            reloadCurrentPlan();
        }, [patientId, reloadCurrentPlan]);

        // Get selected drug/route from the key
        const selectedDrugRoute = React.useMemo(() => {
            if (!selectedDrugRouteKey) return null;
            const [drugID, routeID] = selectedDrugRouteKey.split('-').map(Number);
            return drugRouteCombinations.find(
                (c) => c.drugID === drugID && c.routeID === routeID,
            ) ?? null;
        }, [selectedDrugRouteKey, drugRouteCombinations]);

        // Format drug/route options for Select dropdown (used in both parameter section and medication modal)
        const drugRouteOptions = React.useMemo(() => {
            const lang = i18n.language;
            return drugRouteCombinations.map((c) => ({
                value: `${c.drugID}-${c.routeID}`,
                label: `${lang === 'fr' ? c.drugNameFr : c.drugNameEn} (${lang === 'fr' ? c.routeNameFr : c.routeNameEn})`,
                drugID: c.drugID,
                routeID: c.routeID,
                unitName: c.unitName,
            }));
        }, [drugRouteCombinations, i18n.language]);

        // Fetch dose suggestions when drug is selected and steps exist
        // Handles both global drug selection and per-step custom drug selections
        React.useEffect(() => {
            if (!isDraftMode) {
                const chartData = convertTaperingStepsToChartData(localSteps, i18n.language);
                setTaperingData(chartData);
                setDoseSuggestions([]);
                return;
            }

            // Check if there's any drug to fetch suggestions for
            const hasGlobalDrug = selectedDrugRoute !== null;
            const hasPerStepDrugs = localSteps.some((s) => s.customDrugID && s.customRouteID);

            if ((!hasGlobalDrug && !hasPerStepDrugs) || localSteps.length === 0) {
                setDoseSuggestions([]);
                // Update chart without actual MME data
                const chartData = convertTaperingStepsToChartData(localSteps, i18n.language);
                setTaperingData(chartData);
                return;
            }

            const fetchSuggestions = async () => {
                try {
                    // Compute virtual MME targets (propagating manual edit deltas)
                    // Use ref to always read the latest suggestions and avoid stale closure.
                    // When all steps already have medications and no prior suggestions exist
                    // (e.g. page reload), use planned doseMME directly as targets to avoid
                    // a cascade of re-fetches with progressively shifting virtual targets.
                    const allStepsHaveMeds = localSteps.every(
                        (s) => s.medications && s.medications.length > 0
                    );
                    const hasPriorSuggestions = doseSuggestionsRef.current.length > 0;
                    const virtualMME = (allStepsHaveMeds && !hasPriorSuggestions)
                        ? localSteps.map((s) => roundToNearestHalf(s.doseMME))
                        : computeVirtualMMEArray(localSteps, doseSuggestionsRef.current);

                    // Group steps by their effective drug/route combination
                    const drugGroups = new Map<
                        string,
                        { drugID: number; routeID: number; indices: number[]; targetMMEs: number[] }
                    >();

                    localSteps.forEach((step, index) => {
                        // Determine effective drug for this step
                        const drugID = step.customDrugID ?? selectedDrugRoute?.drugID;
                        const routeID = step.customRouteID ?? selectedDrugRoute?.routeID;

                        if (drugID && routeID) {
                            const key = `${drugID}-${routeID}`;
                            if (!drugGroups.has(key)) {
                                drugGroups.set(key, {
                                    drugID,
                                    routeID,
                                    indices: [],
                                    targetMMEs: [],
                                });
                            }
                            const group = drugGroups.get(key)!;
                            group.indices.push(index);
                            group.targetMMEs.push(virtualMME[index]);
                        }
                    });

                    // Initialize suggestions array with empty results
                    const mergedSuggestions: IDoseSuggestionResult[] = localSteps.map((step) => ({
                        targetMME: step.doseMME,
                        hasSuggestion: false,
                        dosageID: 0,
                        dosageStrength: 0,
                        formType: '',
                        frequencyPerDay: 0,
                        conversionFactor: 0,
                        actualMME: 0,
                    }));

                    // Fetch suggestions for each drug group in parallel
                    const groupPromises = Array.from(drugGroups.values()).map(async (group) => {
                        const suggestions = await MMEApi.suggestDosesBatch({
                            targetMMEs: group.targetMMEs,
                            drugID: group.drugID,
                            routeID: group.routeID,
                        });

                        // Map suggestions back to their original indices
                        suggestions.forEach((suggestion, i) => {
                            const originalIndex = group.indices[i];
                            mergedSuggestions[originalIndex] = suggestion;
                        });
                    });

                    await Promise.all(groupPromises);

                    setDoseSuggestions(mergedSuggestions);

                    // Auto-populate medications for steps that don't have any
                    const stepsNeedingMedications = localSteps.some(
                        (step, index) =>
                            (!step.medications || step.medications.length === 0) &&
                            mergedSuggestions[index]?.hasSuggestion
                    );

                    // Track the effective steps through auto-populate updates
                    // Persist suggestedMME from the latest suggestions (only if changed)
                    const needsSuggestedMMEUpdate = localSteps.some((step, index) => {
                        const suggestion = mergedSuggestions[index];
                        return suggestion?.hasSuggestion && suggestion.actualMME !== step.suggestedMME;
                    });

                    let effectiveSteps = needsSuggestedMMEUpdate
                        ? localSteps.map((step, index) => {
                            const suggestion = mergedSuggestions[index];
                            if (suggestion?.hasSuggestion && suggestion.actualMME !== step.suggestedMME) 
                                return { ...step, suggestedMME: suggestion.actualMME };
                            
                            return step;
                        })
                        : localSteps;

                    if (stepsNeedingMedications) {
                        effectiveSteps = effectiveSteps.map((step, index) => {
                            const suggestion = mergedSuggestions[index];

                            // Only add medication if step has none and suggestion exists
                            if (
                                (!step.medications || step.medications.length === 0) &&
                                suggestion?.hasSuggestion
                            ) {
                                const drugID = step.customDrugID ?? selectedDrugRoute?.drugID;
                                const routeID = step.customRouteID ?? selectedDrugRoute?.routeID;
                                const drugOption = drugRouteOptions.find(
                                    (opt) => opt.drugID === drugID && opt.routeID === routeID
                                );

                                if (drugOption) {
                                    const newMedication: IStepMedication = {
                                        id: `med_${Date.now()}_${index}`,
                                        drugID: drugOption.drugID,
                                        routeID: drugOption.routeID,
                                        drugName: drugOption.label,
                                        unitName: drugOption.unitName,
                                        dosageStrength: suggestion.dosageStrength,
                                        frequencyPerDay: suggestion.frequencyPerDay,
                                        conversionFactor: suggestion.conversionFactor,
                                    };
                                    return { ...step, medications: [newMedication] };
                                }
                            }
                            return step;
                        });
                    }

                    // Only adjust step count when steps were just auto-populated (not on
                    // reload of a saved plan where all steps already have medications).
                    // On reload, the saved plan already has the correct step count;
                    // re-adjusting with freshly-fetched suggestions would cause a cascade.
                    if (!allStepsHaveMeds) {
                        const adjusted = adjustStepsForVirtualMME(
                            effectiveSteps,
                            parameters.originalMME,
                            parameters.reductionPercentage,
                            parameters.daysPerStep,
                            parameters.targetFinalMME,
                            mergedSuggestions,
                        );
                        if (adjusted.length !== effectiveSteps.length) {
                            // Step count changed — update steps; the next useEffect cycle
                            // will fetch dose suggestions for the new/removed steps
                            setLocalSteps(adjusted);
                            setIsCustomized(true);
                            return;
                        }
                    }

                    // Commit step updates (only if changed from original localSteps)
                    if (effectiveSteps !== localSteps) {
                        setLocalSteps(effectiveSteps);
                        // Auto-populated medications are derived data, not user changes.
                        // Only sync savedState when there is no persisted plan yet.
                        if (stepsNeedingMedications && !currentPlan) {
                            setSavedState((prev) =>
                                prev ? { ...prev, steps: [...effectiveSteps] } : null
                            );
                        }
                    }

                    // Update chart with actual MME data and virtual targets
                    const virtualMMEForChart = computeVirtualMMEArray(effectiveSteps, mergedSuggestions);
                    const chartData = convertTaperingStepsToChartData(
                        effectiveSteps,
                        i18n.language,
                        mergedSuggestions,
                        virtualMMEForChart,
                    );
                    setTaperingData(chartData);
                } catch (error) {
                    console.error('Error fetching dose suggestions:', error);
                }
            };

            const timer = setTimeout(fetchSuggestions, 300);
            return () => clearTimeout(timer);
        }, [selectedDrugRoute, localSteps, i18n.language, drugRouteOptions, isDraftMode]);

        // Fetch tapering schedule from backend whenever parameters change
        // Only fetches when NOT customized (auto-generated mode) and when MME calculation is selected
        React.useEffect(() => {
            if (isCustomized) return;
            if (!isDraftMode) return;
            if (!parameters.savedCalculationId) {
                // Clear steps when no MME calculation is selected
                setLocalSteps([]);
                setTaperingData([]);
                return;
            }

            const fetchTaperingSchedule = async () => {
                if (parameters.originalMME <= 0) return;

                setIsLoading(true);
                try {
                    const steps = await OTPApi.calculateTaperingPreview({
                        originalMME: parameters.originalMME,
                        parameters: {
                            reductionPercentage: parameters.reductionPercentage,
                            daysPerStep: parameters.daysPerStep,
                            targetFinalPercentage: 0,
                            targetFinalMME: parameters.targetFinalMME,
                        },
                    });

                    setLocalSteps(steps);
                    // Chart data will be updated by the dose suggestions effect
                    // or directly if no drug is selected
                    if (!selectedDrugRoute) {
                        const chartData = convertTaperingStepsToChartData(steps, i18n.language);
                        setTaperingData(chartData);
                    }
                } catch (error) {
                    console.error('Error fetching tapering schedule:', error);
                } finally {
                    setIsLoading(false);
                }
            };

            const timer = setTimeout(fetchTaperingSchedule, 500);
            return () => clearTimeout(timer);
        }, [parameters, isCustomized, isDraftMode]);

        // Handle step changes from the table (local edit, no backend call)
        const handleStepChange = (updatedSteps: ITaperingStep[]) => {
            // Save current steps to undo history before applying the change
            stepsHistory.push([...localSteps]);

            // Detect which step had a medication change (isManuallyEdited newly set)
            let editedIndex = -1;
            for (let i = 0; i < updatedSteps.length; i++) {
                const oldMeds = JSON.stringify(localSteps[i]?.medications || []);
                const newMeds = JSON.stringify(updatedSteps[i]?.medications || []);
                if (oldMeds !== newMeds && updatedSteps[i]?.isManuallyEdited) {
                    editedIndex = i;
                    break;
                }
            }

            // If medication changed on a manually edited step, clear non-edited subsequent steps'
            // medications so the fetchDoseSuggestions useEffect re-populates with virtual targets
            if (editedIndex >= 0) {
                const cleared = updatedSteps.map((step, i) => {
                    if (i > editedIndex && !step.isManuallyEdited) 
                        return { ...step, medications: [] };
                    
                    return step;
                });
                setLocalSteps(cleared);
            } else 
                setLocalSteps(updatedSteps);

            setIsCustomized(true);
            // Chart update will happen via useEffect after suggestions re-fetch
        };

        // Reset to auto-generated: clear customization and re-fetch from backend
        const handleReset = () => {
            stepsHistory.clear();
            setIsCustomized(false);
        };

        // Compute virtual MME array for passing to table component
        const virtualMMEArray = React.useMemo(() => {
            if (!isDraftMode) return computeVirtualMMEArray(localSteps);
            return computeVirtualMMEArray(localSteps, doseSuggestions);
        }, [localSteps, doseSuggestions, isDraftMode]);

        // Check if any step has manually edited medications (custom changes)
        const hasManuallyEditedMedications = React.useMemo(() => {
            return localSteps.some(
                (step) => step.isManuallyEdited && step.medications && step.medications.length > 0
            );
        }, [localSteps]);

        // Apply drug change - clears all per-step custom drugs and medications
        const applyGlobalDrugChange = (drugRouteKey: string | null) => {
            setSelectedDrugRouteKey(drugRouteKey);

            // Clear all per-step custom drug selections and medications
            const updatedSteps = localSteps.map((step) => ({
                ...step,
                customDrugID: undefined,
                customRouteID: undefined,
                customDosageStrength: undefined,
                customFrequencyPerDay: undefined,
                medications: [], // Clear step medications
                isManuallyEdited: false, // Reset manual edit flag since we're clearing customizations
            }));
            setLocalSteps(updatedSteps);

            // Update chart data
            const chartData = convertTaperingStepsToChartData(updatedSteps, i18n.language, []);
            setTaperingData(chartData);
        };

        // Handle global drug selection change - shows warning if step customizations exist
        const handleGlobalDrugChange = (drugRouteKey: string | null) => {
            // Check if any step has manually edited medications (customizations that would be lost)
            if (hasManuallyEditedMedications) {
                modals.openConfirmModal({
                    title: t('otp.drugChange.warningTitle', 'Change Default Drug'),
                    children: (
                        <Stack gap="sm">
                            <Text>
                                {t(
                                    'otp.drugChange.warningMessage',
                                    'You have customized medications in one or more steps. Changing the default drug will override all step-level medication customizations.'
                                )}
                            </Text>
                            <Text fw="bold">
                                {t('otp.drugChange.warningConfirm', 'Do you want to continue?')}
                            </Text>
                        </Stack>
                    ),
                    labels: {
                        confirm: t('button.continue', 'Continue'),
                        cancel: t('button.cancel', 'Cancel'),
                    },
                    confirmProps: { color: 'orange' },
                    cancelProps: { variant: 'outline', color: 'gray' },
                    onConfirm: () => applyGlobalDrugChange(drugRouteKey),
                });
            } else {
                // No customizations, apply directly
                applyGlobalDrugChange(drugRouteKey);
            }
        };

        // Format saved calculations for select dropdown
        const savedCalculationOptions = React.useMemo(() => {
            return savedCalculations.map((calc) => {
                const date = new Date(calc.calculationDate).toLocaleDateString();
                return {
                    value: String(calc.id),
                    label: `${calc.totalDailyMME} MME (${date}) - ${calc.healthcareProfessionalName}`,
                };
            });
        }, [savedCalculations]);

        const daysPerStepOptions = [
            { value: '7', label: t('otp.parameters.weekly', 'Weekly (7 days)') },
            { value: '14', label: t('otp.parameters.biweekly', 'Bi-weekly (14 days)') },
            { value: '21', label: t('otp.parameters.triweekly', 'Tri-weekly (21 days)') },
            { value: '28', label: t('otp.parameters.monthly', 'Monthly (28 days)') },
        ];

        // Handle selection of saved MME calculation
        const handleSavedCalculationChange = (value: string | null) => {
            if (value) {
                const selectedCalc = savedCalculations.find((calc) => String(calc.id) === value);
                if (selectedCalc) {
                    setIsCustomized(false);
                    setParameters({
                        ...parameters,
                        savedCalculationId: selectedCalc.id,
                        originalMME: selectedCalc.totalDailyMME,
                    });
                }
            } else {
                setParameters({
                    ...parameters,
                    savedCalculationId: null,
                });
            }
        };

        // When parameters change in auto-generated mode, reset customization
        const handleParameterChange = (updates: Partial<TaperingParameters>) => {
            setIsCustomized(false);
            setParameters({ ...parameters, ...updates });
        };

        // Helper function to compare medications arrays
        const areMedicationsEqual = (
            meds1: IStepMedication[] | undefined,
            meds2: IStepMedication[] | undefined
        ): boolean => {
            if (!meds1 && !meds2) return true;
            if (!meds1 || !meds2) return false;
            if (meds1.length !== meds2.length) return false;

            return meds1.every((med1, idx) => {
                const med2 = meds2[idx];
                return (
                    med1.id === med2.id &&
                    med1.drugID === med2.drugID &&
                    med1.routeID === med2.routeID &&
                    med1.dosageStrength === med2.dosageStrength &&
                    med1.frequencyPerDay === med2.frequencyPerDay &&
                    med1.timeOfDay === med2.timeOfDay &&
                    med1.notes === med2.notes
                );
            });
        };

        // Check if there are any changes compared to the saved state
        const hasChanges = React.useMemo(() => {
            if (!savedState) {
                // If no saved state exists, there are changes if we have steps
                return localSteps.length > 0;
            }

            // Compare parameters
            const parametersChanged =
                parameters.originalMME !== savedState.parameters.originalMME ||
                parameters.savedCalculationId !== savedState.parameters.savedCalculationId ||
                parameters.reductionPercentage !== savedState.parameters.reductionPercentage ||
                parameters.daysPerStep !== savedState.parameters.daysPerStep ||
                parameters.targetFinalMME !== savedState.parameters.targetFinalMME;

            // Compare drug/route selection
            const drugRouteChanged = selectedDrugRouteKey !== savedState.drugRouteKey;

            // Compare steps (length and content including medications)
            const stepsChanged =
                localSteps.length !== savedState.steps.length ||
                localSteps.some((step, index) => {
                    const savedStep = savedState.steps[index];
                    return (
                        step.stepNumber !== savedStep?.stepNumber ||
                        step.doseMME !== savedStep?.doseMME ||
                        step.dosePercentageOfOriginal !== savedStep?.dosePercentageOfOriginal ||
                        step.startDate !== savedStep?.startDate ||
                        step.durationDays !== savedStep?.durationDays ||
                        step.isManuallyEdited !== savedStep?.isManuallyEdited ||
                        !areMedicationsEqual(step.medications, savedStep?.medications)
                    );
                });

            return parametersChanged || drugRouteChanged || stepsChanged;
        }, [savedState, parameters, selectedDrugRouteKey, localSteps]);

        // Internal save logic (called after name is provided for new plans)
        const executeSave = async (planName?: string) => {
            setIsSaving(true);
            try {
                // Extract drug/route IDs from selectedDrugRouteKey
                let selectedDrugID: number | undefined;
                let selectedRouteID: number | undefined;
                if (selectedDrugRouteKey) {
                    const [drugID, routeID] = selectedDrugRouteKey.split('-').map(Number);
                    selectedDrugID = drugID;
                    selectedRouteID = routeID;
                }

                const otpParams = {
                    reductionPercentage: parameters.reductionPercentage,
                    daysPerStep: parameters.daysPerStep,
                    targetFinalPercentage: 0,
                    targetFinalMME: parameters.targetFinalMME,
                    selectedDrugID,
                    selectedRouteID,
                };

                // If no OTP ID exists, generate a new OTP first
                let targetOtpId = otpId;
                if (!targetOtpId) {
                    const newOTP = await OTPApi.generateOTP({
                        patientID: patientId!,
                        savedMMECalculationID: parameters.savedCalculationId!,
                        name: planName,
                        parameters: otpParams,
                    });
                    targetOtpId = newOTP.id;
                    setOtpId(newOTP.id);
                    setCurrentPlan(newOTP);
                }

                // Save tapering plan (steps + parameters)
                await OTPApi.saveTaperingPlan(targetOtpId, {
                    savedMMECalculationID: parameters.savedCalculationId ?? undefined,
                    parameters: otpParams,
                    taperingSteps: localSteps,
                });

                notifications.show({
                    title: t('otp.save.success', 'Success'),
                    message: t('otp.save.planSaved', 'Tapering plan saved successfully'),
                    color: 'green',
                });

                // Update saved state after successful save
                setSavedState({
                    parameters: { ...parameters },
                    steps: [...localSteps],
                    drugRouteKey: selectedDrugRouteKey,
                });

                // Reload current plan to reflect changes
                await reloadCurrentPlan();
            } catch (error: any) {
                console.error('Error saving tapering plan:', error);
                notifications.show({
                    title: t('otp.save.error', 'Error'),
                    message: error?.message || t('otp.save.failedToSave', 'Failed to save tapering plan'),
                    color: 'red',
                });
            } finally {
                setIsSaving(false);
            }
        };

        // Save tapering plan
        const handleSave = async () => {
            if (!parameters.savedCalculationId) {
                notifications.show({
                    title: t('otp.save.error', 'Error'),
                    message: t('otp.save.selectMME', 'Please select an MME calculation before saving'),
                    color: 'red',
                });
                return;
            }

            if (localSteps.length === 0) {
                notifications.show({
                    title: t('otp.save.error', 'Error'),
                    message: t('otp.save.noSteps', 'No tapering steps to save'),
                    color: 'red',
                });
                return;
            }

            // For new plans, prompt for a name first
            if (!otpId) {
                let planNameValue = '';
                modals.open({
                    title: t('otp.save.nameModal.title', 'Name Your Plan'),
                    children: (
                        <Stack gap="md">
                            <TextInput
                                label={t('otp.save.nameModal.label', 'Plan Name')}
                                placeholder={t('otp.save.nameModal.placeholder', 'e.g. Initial Tapering Plan')}
                                onChange={(e) => { planNameValue = e.currentTarget.value; }}
                                data-autofocus
                            />
                            <Group justify="flex-end">
                                <Button variant="default" onClick={() => modals.closeAll()}>
                                    {t('common.cancel', 'Cancel')}
                                </Button>
                                <Button onClick={() => { modals.closeAll(); executeSave(planNameValue || undefined); }}>
                                    {t('otp.save.button', 'Save Plan')}
                                </Button>
                            </Group>
                        </Stack>
                    ),
                });
                return;
            }

            // Existing plan — save directly
            await executeSave();
        };

        // Reset state to create a new plan
        const resetToNewPlan = () => {
            stepsHistory.clear();
            setOtpId(null);
            setCurrentPlan(null);
            setLocalSteps([]);
            setTaperingData([]);
            setIsCustomized(false);
            setSelectedDrugRouteKey(null);
            setParameters({
                originalMME: 100,
                savedCalculationId: null,
                reductionPercentage: 10,
                daysPerStep: 7,
                targetFinalMME: 1,
            });
            setSavedState(null);
        };

        // Delete plan with confirmation modal
        const handleDeletePlan = () => {
            if (!currentPlan) return;

            modals.openConfirmModal({
                title: t('otp.planManager.delete.title', 'Delete Plan'),
                children: (
                    <Stack gap="sm">
                        <Text>
                            {t(
                                'otp.planManager.delete.message',
                                'Are you sure you want to delete this tapering plan? This action cannot be undone.'
                            )}
                        </Text>
                        <Text fw="bold" c="dimmed" size="sm">
                            {t('otp.planManager.delete.planInfo', 'Plan created on {{date}}', {
                                date: new Date(currentPlan.createdDate).toLocaleDateString(),
                            })}
                        </Text>
                    </Stack>
                ),
                labels: {
                    confirm: t('button.delete', 'Delete'),
                    cancel: t('button.cancel', 'Cancel'),
                },
                confirmProps: { color: 'red' },
                cancelProps: { variant: 'outline', color: 'gray' },
                onConfirm: async () => {
                    try {
                        await deleteOTPMutation.mutateAsync(currentPlan.id);
                        resetToNewPlan();
                    } catch {
                        // Error notification handled by mutation hook
                    }
                },
            });
        };

        // Open outcome dialog for a specific action
        const handleChangeStatus = (action: OutcomeAction) => {
            if (!currentPlan) return;
            setPendingOutcomePlan(currentPlan);
            setOutcomeDialogAction(action);
            setOutcomeDialogOpen(true);
        };

        // Handle outcome dialog confirmation
        const handleOutcomeConfirm = async (result: IOutcomeDialogResult) => {
            if (!pendingOutcomePlan) return;

            const outcomeMutations = { complete: completeOTPMutation, abort: abortOTPMutation, append: appendOTPMutation };
            const mutation = outcomeMutations[result.action];

            try {
                if (mutation) {
                    await mutation.mutateAsync({
                        id: pendingOutcomePlan.id,
                        reasonCode: result.reasonCode,
                        reasonText: result.reasonText,
                    });
                }

                await reloadCurrentPlan();
            } catch {
                // Error notification handled by mutation hook
            } finally {
                setPendingOutcomePlan(null);
            }
        };

        const handleOutcomeDialogClose = () => {
            setOutcomeDialogOpen(false);
            setPendingOutcomePlan(null);
        };

        // Submit plan for validation - opens validation responsibility dialog first
        const handleSubmitForValidation = () => {
            if (!currentPlan) return;
            setPendingSubmitPlan(currentPlan);
            setValidationResponsibilityDialogOpen(true);
        };

        const handleValidationResponsibilityConfirm = async (result: IValidationResponsibilityResult) => {
            setValidationResponsibilityDialogOpen(false);
            if (result.responsibility === 'self') 
                setCoMedicationModalOpen(true);
             else if (result.responsibility === 'delegate' && result.delegatedHcpId && pendingSubmitPlan) {
                try {
                    await delegateForValidationMutation.mutateAsync({
                        id: pendingSubmitPlan.id,
                        delegatedToHcpId: result.delegatedHcpId,
                    });
                    console.log(`[NOTIFICATION] HCP ${result.delegatedHcpId} will be notified with link to OTP plan ${pendingSubmitPlan.id}`);
                    setPendingSubmitPlan(null);
                    await reloadCurrentPlan();
                } catch {
                    // Error handled by mutation
                }
            }
        };

        const handleValidationResponsibilityDialogClose = () => {
            setValidationResponsibilityDialogOpen(false);
            setPendingSubmitPlan(null);
        };

        // Handle validation by delegated HCP - opens co-medication modal directly
        const handleValidateByDelegatedHcp = () => {
            if (!currentPlan) return;
            setPendingSubmitPlan(currentPlan);
            setCoMedicationModalOpen(true);
        };

        // Called after co-medication checklist saved by delegated HCP
        const handleDelegatedHcpChecklistSubmit = async () => {
            if (!pendingSubmitPlan) return;

            try {
                await validateByDelegatedHcpMutation.mutateAsync(pendingSubmitPlan.id);
                setCoMedicationModalOpen(false);
                setPendingSubmitPlan(null);
                await reloadCurrentPlan();
            } catch {
                // Error handled by mutation
            }
        };

        // Called after co-medication checklist is saved successfully
        const handleCoMedicationChecklistSubmit = async () => {
            if (!pendingSubmitPlan) return;

            try {
                await submitForValidationMutation.mutateAsync(pendingSubmitPlan.id);
                setCoMedicationModalOpen(false);
                setPendingSubmitPlan(null);
                await reloadCurrentPlan();
            } catch {
                // Error notification handled by mutation hook
            }
        };

        const handleCoMedicationModalClose = () => {
            setCoMedicationModalOpen(false);
            setPendingSubmitPlan(null);
        };

        // Return plan to draft
        const handleReturnToDraft = async () => {
            if (!currentPlan) return;
            try {
                await returnToDraftMutation.mutateAsync(currentPlan.id);
                await reloadCurrentPlan();
            } catch {
                // Error notification handled by mutation hook
            }
        };

        // Activate plan - open acknowledgment modal
        const handleActivatePlan = () => {
            if (!currentPlan) return;
            setAcknowledgmentModalOpen(true);
        };

        // Called after patient acknowledgment is confirmed
        const handleActivateConfirmed = async (note?: string) => {
            if (!currentPlan) return;
            try {
                await activateOTPMutation.mutateAsync({
                    id: currentPlan.id,
                    patientAcknowledged: true,
                    patientAcknowledgmentNote: note,
                });
                setAcknowledgmentModalOpen(false);
                await reloadCurrentPlan();
            } catch {
                // Error notification handled by mutation hook
            }
        };

        // Check if any drug is selected (either global or per-step)
        const hasDrugSelected =
            selectedDrugRoute !== null ||
            localSteps.some((step) => step.customDrugID && step.customRouteID);

        // Determine if the current plan can be edited
        // Plans in PendingValidation or Active status should not be editable
        const isPlanLocked = currentPlan?.status === OTPStatus.PendingValidation || currentPlan?.status === OTPStatus.PendingHcpValidation || currentPlan?.status === OTPStatus.Active;

        // Whether the current plan is terminal (Completed, Aborted, or any unrecognized status)
        const isPlanTerminal = currentPlan !== null &&
            currentPlan.status !== OTPStatus.Draft &&
            currentPlan.status !== OTPStatus.PendingValidation &&
            currentPlan.status !== OTPStatus.PendingHcpValidation &&
            currentPlan.status !== OTPStatus.Active;

        const canEditCurrentPlan = !isPlanLocked && !isPlanTerminal && (currentPlan?.canEdit ?? true);

        // Undo/redo step changes
        const handleUndo = React.useCallback(() => {
            if (!canEditCurrentPlan) return;
            const prev = stepsHistory.undo(localSteps);
            if (prev) {
                setLocalSteps(prev);
                setIsCustomized(true);
            }
        }, [localSteps, canEditCurrentPlan, stepsHistory]);

        const handleRedo = React.useCallback(() => {
            if (!canEditCurrentPlan) return;
            const next = stepsHistory.redo(localSteps);
            if (next) {
                setLocalSteps(next);
                setIsCustomized(true);
            }
        }, [localSteps, canEditCurrentPlan, stepsHistory]);

        useHotkeys([
            ['mod+z', handleUndo],
            ['mod+shift+z', handleRedo],
        ]);

        const getStatusLabel = useGetStatusLabel();

        // Render inline action buttons based on current plan status
        const renderStatusActions = () => {
            if (!currentPlan) return null;

            switch (currentPlan.status) {
                case OTPStatus.Draft:
                    return (
                        <Group gap="xs">
                            {!hasChanges && (
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="blue"
                                    leftSection={<IconSend size={16} />}
                                    onClick={handleSubmitForValidation}
                                    
                                >
                                    {t('otp.planManager.actions.submitForValidation', 'Submit for Validation')}
                                </Button>
                            )}
                            {hasChanges && (
                                <Button
                                    size="xs"
                                    leftSection={<IconDeviceFloppy size={14} />}
                                    onClick={handleSave}
                                    loading={isSaving}
                                    disabled={!parameters.savedCalculationId || localSteps.length === 0 || !hasChanges}
                                >
                                    {t('otp.save.button', 'Save Plan')}
                                </Button>
                            )}
                            <MantineTooltip label={t('otp.planManager.actions.delete', 'Delete')}>
                                <ActionIcon variant="subtle" color="red" size="sm" onClick={handleDeletePlan}>
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </MantineTooltip>
                        </Group>
                    );
                case OTPStatus.PendingHcpValidation:
                case OTPStatus.PendingValidation: {
                    const returnToDraftBtn = (
                        <Button size="xs" variant="subtle" color="gray" leftSection={<IconArrowBack size={16} />} onClick={handleReturnToDraft}>
                            {t('otp.planManager.actions.returnToDraft', 'Return to Draft')}
                        </Button>
                    );
                    const abortBtn = (
                        <Button size="xs" variant="subtle" color="red" leftSection={<IconPlayerStop size={16} />} onClick={() => handleChangeStatus('abort')}>
                            {t('otp.planManager.actions.abort', 'Abort')}
                        </Button>
                    );
                    return (
                        <Group gap="xs">
                            {currentPlan.status === OTPStatus.PendingHcpValidation && currentPlan.canValidate && (
                                <Button size="xs" variant="subtle" color="green" leftSection={<IconCheck size={16} />} onClick={handleValidateByDelegatedHcp}>
                                    {t('otp.planManager.actions.validate', 'Validate')}
                                </Button>
                            )}
                            {currentPlan.status === OTPStatus.PendingValidation && (
                                <Button size="xs" variant="subtle" color="green" leftSection={<IconPlayerPlay size={16} />} onClick={handleActivatePlan}>
                                    {t('otp.planManager.actions.activate', 'Activate')}
                                </Button>
                            )}
                            {returnToDraftBtn}
                            {abortBtn}
                        </Group>
                    );
                }
                case OTPStatus.Active:
                    return (
                        <Group gap="xs">
                            <Button
                                size="xs"
                                variant="subtle"
                                leftSection={<IconPrinter size={16} />}
                                onClick={() => downloadPdfMutation.mutate({ id: currentPlan.id, language: i18n.language, planName: currentPlan.name })}
                                loading={downloadPdfMutation.isPending}
                            >
                                {t('otp.planManager.actions.print', 'Print')}
                            </Button>
                            <Menu shadow="md" width={200}>
                                <Menu.Target>
                                    <Button size="xs" variant="subtle" rightSection={<IconChevronDown size={14} />}>
                                        {t('otp.planManager.actions.changeStatus', 'Change Status')}
                                    </Button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    <Menu.Item onClick={() => handleChangeStatus('complete')}>
                                        {t('otp.outcomeDialog.options.complete')}
                                    </Menu.Item>
                                    <Menu.Item onClick={() => handleChangeStatus('append')}>
                                        {t('otp.outcomeDialog.options.append')}
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Item color="red" onClick={() => handleChangeStatus('abort')}>
                                        {t('otp.outcomeDialog.options.abort')}
                                    </Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        </Group>
                    );
                default:
                    return null;
            }
        };

        return (
            <Paper
                h={'100%'}
                p="md"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Stack h={'100%'} gap="md">
                    <AppMedWarning storageKey="warning-otp-plan-dismissed" messageKey="warning.otpDisclaimer" />
                    {/* Plan Status - Collapsible */}
                    <Box style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}>
                        {/* Status Header Bar */}
                        <Flex
                            p="md"
                            justify="space-between"
                            align="center"
                            style={{
                                backgroundColor: '#f9f9f9',
                                borderRadius: isStatusExpanded ? '4px 4px 0 0' : 4,
                            }}
                        >
                            <Flex
                                gap="sm"
                                align="center"
                                wrap="wrap"
                                style={{ flex: 1, cursor: 'pointer' }}
                                onClick={() => setIsStatusExpanded(!isStatusExpanded)}
                            >
                                <Title order={5}>
                                    {t('otp.status.title', 'Status')}
                                </Title>
                                <Box style={{ width: 1, height: 24, backgroundColor: '#d0d0d0' }} />
                                {currentPlan ? (
                                    <>
                                        <Badge size="lg" color={getStatusColor(currentPlan.status)} variant="filled">
                                            {getStatusLabel(currentPlan.status)}
                                        </Badge>
                                        {currentPlan.name && (
                                            <Text size="sm" fw={500}>
                                                {currentPlan.name}
                                            </Text>
                                        )}
                                        <Text size="sm" c="dimmed">
                                            {currentPlan.originalMME} {'→'} {currentPlan.parameters?.targetFinalMME ?? 1} MME
                                        </Text>
                                    </>
                                ) : (
                                    <Text size="sm" c="dimmed">
                                        {isLoadingPlan
                                            ? t('otp.planManager.loading', 'Loading...')
                                            : t('otp.planManager.empty.message', 'No tapering plan')}
                                    </Text>
                                )}
                            </Flex>
                            <Group gap="sm" wrap="nowrap">
                                {currentPlan ? (
                                    isPlanTerminal ? (
                                        <Button
                                            size="xs"
                                            leftSection={<IconPlus size={14} />}
                                            onClick={resetToNewPlan}
                                        >
                                            {t('otp.planManager.createNew', 'Create New Plan')}
                                        </Button>
                                    ) : (
                                        renderStatusActions()
                                    )
                                ) : (
                                    !isLoadingPlan && (
                                        <Button
                                            size="xs"
                                            leftSection={<IconDeviceFloppy size={14} />}
                                            onClick={handleSave}
                                            loading={isSaving}
                                            disabled={!parameters.savedCalculationId || localSteps.length === 0 || !hasChanges}
                                        >
                                            {t('otp.save.button', 'Save Plan')}
                                        </Button>
                                    )
                                )}
                                <IconChevronDown
                                    size={20}
                                    style={{
                                        cursor: 'pointer',
                                        transform: isStatusExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 300ms ease',
                                    }}
                                    onClick={() => setIsStatusExpanded(!isStatusExpanded)}
                                />
                            </Group>
                        </Flex>

                        {/* Expanded Status Content */}
                        <Collapse in={isStatusExpanded} transitionDuration={300}>
                            <Box p="md" style={{ borderTop: '1px solid #e0e0e0' }}>
                                {currentPlan ? (
                                    <>
                                        <Group gap="sm">
                                            {currentPlan.status === OTPStatus.PendingHcpValidation && currentPlan.delegatedToHcpName && (
                                                <Text size="sm" fw={500}>
                                                    {t('otp.planManager.delegatedTo', 'Delegated to')}: {currentPlan.delegatedToHcpName}
                                                </Text>
                                            )}
                                        </Group>
                                        <OTPEventTimeline otpId={currentPlan.id} />
                                    </>
                                ) : (
                                    isLoadingPlan && <Loader size="sm" />
                                )}
                            </Box>
                        </Collapse>
                    </Box>

                    {/* Tapering Parameters + Graph + Steps wrapper with MME overlay */}
                    <Box style={{ position: 'relative' }}>
                    {showNoMMEOverlay && (
                        <Overlay
                            backgroundOpacity={0.75}
                            color="#fff"
                            blur={3}
                            zIndex={10}
                            radius="sm"
                        >
                            <Flex
                                direction="column"
                                align="center"
                                justify="center"
                                h="100%"
                                gap="md"
                                p="xl"
                            >
                                <IconCalculator size={48} color="var(--mantine-color-blue-6)" />
                                <Title order={4} ta="center">
                                    {t('otp.noMME.title', 'MME Calculation Required')}
                                </Title>
                                <Text size="sm" c="dimmed" ta="center" maw={400}>
                                    {t('otp.noMME.description', 'An MME calculation must be created before configuring the tapering plan parameters.')}
                                </Text>
                                <Button
                                    leftSection={<IconCalculator size={16} />}
                                    onClick={onNavigateToMMETab}
                                >
                                    {t('otp.noMME.cta', 'Go to MME Calculator')}
                                </Button>
                            </Flex>
                        </Overlay>
                    )}

                    {/* Tapering Parameters - Collapsible */}
                    <Box style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}>
                        {/* Collapsed Summary Bar */}
                        <Flex
                            p="md"
                            justify="space-between"
                            align="center"
                            style={{
                                cursor: 'pointer',
                                backgroundColor: '#f9f9f9',
                                borderRadius: isParametersExpanded ? '4px 4px 0 0' : 4,
                            }}
                            onClick={() => setIsParametersExpanded(!isParametersExpanded)}
                        >
                            <Flex gap="xl" align="center" wrap="wrap" style={{ flex: 1 }}>
                                <Title order={5}>
                                    {t('otp.parameters.title', 'OTP Parameters')}
                                </Title>
                                <Box style={{ width: 1, height: 24, backgroundColor: '#d0d0d0' }} />
                                <Box>
                                    <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                                        {t('otp.parameters.originalMME', 'Starting')}
                                    </Text>
                                    <Text size="sm" fw={600}>
                                        {parameters.originalMME} MME
                                    </Text>
                                </Box>
                                <Box>
                                    <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                                        {t('otp.parameters.targetFinalMME', 'Target')}
                                    </Text>
                                    <Text size="sm" fw={600}>
                                        {parameters.targetFinalMME} MME
                                    </Text>
                                </Box>
                                <Box>
                                    <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                                        {t('otp.parameters.reductionPercentage', 'Reduction')}
                                    </Text>
                                    <Text size="sm" fw={600}>
                                        {parameters.reductionPercentage}% {daysPerStepOptions.find(o => o.value === String(parameters.daysPerStep))?.label.toLowerCase()}
                                    </Text>
                                </Box>
                                {selectedDrugRoute && (
                                    <Box>
                                        <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                                            {t('otp.parameters.drug', 'Drug')}
                                        </Text>
                                        <Text size="sm" fw={600}>
                                            {i18n.language === 'fr' ? selectedDrugRoute.drugNameFr : selectedDrugRoute.drugNameEn} ({i18n.language === 'fr' ? selectedDrugRoute.routeNameFr : selectedDrugRoute.routeNameEn})
                                        </Text>
                                    </Box>
                                )}
                            </Flex>
                            <Flex gap="sm" align="center">
                                <IconChevronDown
                                    size={20}
                                    style={{
                                        transform: isParametersExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 300ms ease',
                                    }}
                                />
                            </Flex>
                        </Flex>

                        {/* Expanded Form */}
                        <Collapse in={isParametersExpanded} transitionDuration={300}>
                            <Box p="md" style={{ borderTop: '1px solid #e0e0e0' }}>
                                <Stack gap="sm">
                                    <Flex gap="md" wrap="wrap">
                                        <Select
                                            label={t('otp.parameters.originalMME', 'Original MME')}
                                            description={t('otp.parameters.originalMMEDesc', 'Select a saved MME calculation')}
                                            placeholder={t('otp.parameters.selectMME', 'Select an MME calculation')}
                                            data={savedCalculationOptions}
                                            value={parameters.savedCalculationId ? String(parameters.savedCalculationId) : null}
                                            onChange={handleSavedCalculationChange}
                                            disabled={isLoadingCalculations || !canEditCurrentPlan}
                                            searchable
                                            clearable
                                            style={{ flex: '1 1 200px' }}
                                        />

                                        <NumberInput
                                            label={t('otp.parameters.reductionPercentage', 'Reduction per Step')}
                                            description={t('otp.parameters.reductionPercentageDesc', 'Percentage to reduce dose')}
                                            value={parameters.reductionPercentage}
                                            onChange={(val) => handleParameterChange({ reductionPercentage: Number(val) || 0 })}
                                            min={0.1}
                                            step={1}
                                            suffix="%"
                                            style={{ flex: '1 1 200px' }}
                                            disabled={!canEditCurrentPlan}
                                        />
                                    </Flex>

                                    <Flex gap="md" wrap="wrap">
                                        <Select
                                            label={t('otp.parameters.daysPerStep', 'Step Duration')}
                                            description={t('otp.parameters.daysPerStepDesc', 'Days at each dose level')}
                                            data={daysPerStepOptions}
                                            value={String(parameters.daysPerStep)}
                                            onChange={(val) => handleParameterChange({ daysPerStep: Number(val) || 7 })}
                                            style={{ flex: '1 1 200px' }}
                                            disabled={!canEditCurrentPlan}
                                        />

                                        <NumberInput
                                            label={t('otp.parameters.targetFinalMME', 'Target Final Dose')}
                                            description={t('otp.parameters.targetFinalMMEDesc', 'Final dose in MME')}
                                            value={parameters.targetFinalMME}
                                            onChange={(val) => handleParameterChange({ targetFinalMME: Number(val) || 0 })}
                                            min={1}
                                            max={parameters.originalMME}
                                            step={5}
                                            suffix=" MME"
                                            style={{ flex: '1 1 200px' }}
                                            disabled={!canEditCurrentPlan}
                                        />
                                    </Flex>

                                    {/* Drug Selection */}
                                    <Select
                                        label={t('otp.parameters.drug', 'Drug')}
                                        description={t('otp.parameters.drugDesc', 'Select a drug for the tapering plan')}
                                        placeholder={t('otp.parameters.selectDrug', 'Select a drug')}
                                        data={drugRouteOptions}
                                        value={selectedDrugRouteKey}
                                        onChange={handleGlobalDrugChange}
                                        searchable
                                        clearable
                                        disabled={!canEditCurrentPlan}
                                    />
                                </Stack>
                            </Box>
                        </Collapse>
                    </Box>

                    {/* Line Graph - Collapsible */}
                    <Box style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}>
                        {/* Graph Header Bar */}
                        <Flex
                            p="md"
                            justify="space-between"
                            align="center"
                            style={{
                                cursor: 'pointer',
                                backgroundColor: '#f9f9f9',
                                borderRadius: isGraphExpanded ? '4px 4px 0 0' : 4,
                            }}
                            onClick={() => setIsGraphExpanded(!isGraphExpanded)}
                        >
                            <Group gap="sm" style={{ flex: 1 }} align="center">
                                <Title order={5}>
                                    {t('otp.graph.title', 'Tapering Schedule')}
                                </Title>
                                {localSteps.length > 0 && (
                                    <>
                                        <Box style={{ width: 1, height: 24, backgroundColor: '#d0d0d0' }} />
                                        {!isGraphExpanded && taperingData.length > 1 && (
                                            <Box style={{ width: graphContainerWidth > 0 ? Math.round(graphContainerWidth * 28 / 300) : 100, height: 28, backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={taperingData} margin={{ top: 4, right: 4, left: 4, bottom: 4 }} accessibilityLayer={false}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                        <XAxis dataKey="date" type="number" scale="time" domain={['dataMin', 'dataMax']} hide />
                                                        <Line
                                                            type="linear"
                                                            dataKey="mme"
                                                            stroke="#1C7ED6"
                                                            strokeWidth={1.5}
                                                            dot={false}
                                                            isAnimationActive={false}
                                                        />
                                                        {hasDrugSelected && (
                                                            <Line
                                                                type="linear"
                                                                dataKey="actualMME"
                                                                stroke="#2f9e44"
                                                                strokeWidth={1.5}
                                                                dot={false}
                                                                isAnimationActive={false}
                                                                connectNulls={false}
                                                            />
                                                        )}
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Group>
                            <IconChevronDown
                                size={20}
                                style={{
                                    transform: isGraphExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 300ms ease',
                                }}
                            />
                        </Flex>

                        {/* Expanded Graph */}
                        <Collapse in={isGraphExpanded} transitionDuration={300}>
                            <Box p="md" style={{ borderTop: '1px solid #e0e0e0' }}>
                                <Box ref={graphContainerRef} style={{ height: 300, position: 'relative' }}>
                                    {isLoading && (
                                        <Box style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
                                            <Loader size="lg" />
                                        </Box>
                                    )}
                                    {!isLoading && localSteps.length === 0 && (
                                        <Box
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                zIndex: 10,
                                                backdropFilter: 'blur(2px)',
                                            }}
                                        >
                                            <Paper p="xl" shadow="md" style={{ maxWidth: 500, textAlign: 'center' }}>
                                                <Stack gap="md">
                                                    <Title order={4}>
                                                        {t('otp.graph.noData.title', 'Get Started with Tapering Plan')}
                                                    </Title>
                                                    <Text size="sm" c="dimmed">
                                                        {t('otp.graph.noData.description', 'To create a tapering plan:')}
                                                    </Text>
                                                    <Stack gap="xs" align="flex-start" style={{ textAlign: 'left' }}>
                                                        <Text size="sm">
                                                            1. {t('otp.graph.noData.step1', 'Select a saved MME calculation below')}
                                                        </Text>
                                                        <Text size="sm">
                                                            2. {t('otp.graph.noData.step2', 'Adjust reduction percentage and step duration')}
                                                        </Text>
                                                        <Text size="sm">
                                                            3. {t('otp.graph.noData.step3', 'Optionally select a drug for dose suggestions')}
                                                        </Text>
                                                        <Text size="sm">
                                                            4. {t('otp.graph.noData.step4', 'Review and customize the tapering steps')}
                                                        </Text>
                                                    </Stack>
                                                </Stack>
                                            </Paper>
                                        </Box>
                                    )}
                                    <ResponsiveContainer width="100%" height="100%" >
                                        <LineChart
                                            accessibilityLayer={false}
                                            data={taperingData}
                                            margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

                                            <XAxis
                                                dataKey="date"
                                                type="number"
                                                scale="time"
                                                domain={['dataMin', 'dataMax']}
                                                tickFormatter={(ts) => formatDateTick(ts, i18n.language)}
                                                label={{ value: t('otp.graph.xaxis', 'Date'), position: 'insideBottom', offset: -10 }}
                                                stroke="#666"
                                            />

                                            <YAxis
                                                label={{ value: t('otp.graph.yaxis', 'MME Dose'), angle: -90, position: 'insideLeft' }}
                                                stroke="#666"
                                            />

                                            <Tooltip content={CustomTooltip} />

                                            <Legend verticalAlign="top" height={36} />

                                            {/* Upper bound (dashed line) */}
                                            <Line
                                                type="monotone"
                                                dataKey="upperBound"
                                                stroke="#90caf9"
                                                strokeWidth={1}
                                                strokeDasharray="5 5"
                                                dot={false}
                                                activeDot={false}
                                                name={t('otp.graph.legend.upperRange', 'Upper Range')}
                                                legendType="none"
                                            />

                                            {/* Main tapering line (target MME) */}
                                            <Line
                                                type="linear"
                                                dataKey="mme"
                                                stroke="#1C7ED6"
                                                strokeWidth={3}
                                                dot={{ fill: '#1C7ED6', r: 4 }}
                                                activeDot={{ r: 6 }}
                                                name={t('otp.graph.legend.targetMME', 'Planned MME')}
                                            />

                                            {/* Virtual MME line (dashed, shown when deltas propagate) */}
                                            {hasDrugSelected && taperingData.some((d) => d.virtualMME !== undefined) && (
                                                <Line
                                                    type="linear"
                                                    dataKey="virtualMME"
                                                    stroke="#9c27b0"
                                                    strokeWidth={2}
                                                    strokeDasharray="6 3"
                                                    dot={{ fill: '#9c27b0', r: 4 }}
                                                    activeDot={{ r: 5 }}
                                                    name={t('otp.graph.legend.virtualMME', 'Virtual MME')}
                                                    connectNulls={false}
                                                />
                                            )}

                                            {/* Actual MME line (with variance-colored dots) */}
                                            {hasDrugSelected && (
                                                <Line
                                                    type="linear"
                                                    dataKey="actualMME"
                                                    stroke="#2f9e44"
                                                    strokeWidth={2}
                                                    dot={<CustomActualMMEDot />}
                                                    activeDot={<CustomActualMMEActiveDot />}
                                                    name={t('otp.graph.legend.actualMME', 'Actual MME')}
                                                    connectNulls={false}
                                                />
                                            )}

                                            {/* Lower bound (dashed line) */}
                                            <Line
                                                type="monotone"
                                                dataKey="lowerBound"
                                                stroke="#90caf9"
                                                strokeWidth={1}
                                                strokeDasharray="5 5"
                                                dot={false}
                                                activeDot={false}
                                                name={t('otp.graph.legend.lowerRange', 'Lower Range')}
                                                legendType="none"
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>

                    {/* Editable Steps Table - Only show when there are steps */}
                    {localSteps.length > 0 && (
                        <TaperingStepsTable
                            steps={localSteps}
                            originalMME={parameters.originalMME}
                            isCustomized={isCustomized}
                            onStepChange={handleStepChange}
                            onReset={handleReset}
                            onUndo={handleUndo}
                            onRedo={handleRedo}
                            canUndo={stepsHistory.canUndo}
                            canRedo={stepsHistory.canRedo}
                            doseSuggestions={hasDrugSelected ? doseSuggestions : undefined}
                            drugRouteOptions={drugRouteOptions}
                            globalDrugRouteKey={selectedDrugRouteKey}
                            disabled={!canEditCurrentPlan}
                            forceExpanded={!currentPlan && localSteps.length > 0 ? true : undefined}
                            virtualMMEArray={virtualMMEArray}
                        />
                    )}
                    </Box>
                </Stack>

                {/* Validation Responsibility Dialog */}
                {pendingSubmitPlan && (
                    <ValidationResponsibilityDialog
                        opened={validationResponsibilityDialogOpen}
                        onClose={handleValidationResponsibilityDialogClose}
                        onConfirm={handleValidationResponsibilityConfirm}
                        patientId={patientId!}
                    />
                )}

                {/* Co-Medication Checklist Modal */}
                {pendingSubmitPlan && (
                    <CoMedicationChecklistModal
                        opened={coMedicationModalOpen}
                        onClose={handleCoMedicationModalClose}
                        otpId={pendingSubmitPlan.id}
                        onSubmitSuccess={
                            pendingSubmitPlan.status === OTPStatus.PendingHcpValidation
                                ? handleDelegatedHcpChecklistSubmit
                                : handleCoMedicationChecklistSubmit
                        }
                    />
                )}

                {/* Patient Acknowledgment Modal for Activation */}
                <PatientAcknowledgmentModal
                    opened={acknowledgmentModalOpen}
                    onClose={() => setAcknowledgmentModalOpen(false)}
                    onConfirm={handleActivateConfirmed}
                    isLoading={activateOTPMutation.isPending}
                />

                {/* Outcome Dialog for Complete/Abort */}
                <OTPOutcomeDialog
                    opened={outcomeDialogOpen}
                    onClose={handleOutcomeDialogClose}
                    onConfirm={handleOutcomeConfirm}
                    action={outcomeDialogAction}
                />
            </Paper>
        );
    }),
);

// Export the component directly since it's already memoized by observer
export const OTPParametersSection = OTPParametersSectionComponent;
